import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Trash2, Plus, AlarmClock } from 'lucide-react';
import { useAdminLang } from '../../hooks/useAdminLang';

const CATS = {
  fiscal: { label: 'Fiscal', cls: 'bg-amber-500/20 text-amber-300' },
  social: { label: 'Social', cls: 'bg-sky-500/20 text-sky-300' },
  legal: { label: 'Légal', cls: 'bg-violet-500/20 text-violet-300' },
  api_payment: { label: 'Paiement app/API', cls: 'bg-rose-500/20 text-rose-300' },
  other: { label: 'Autre', cls: 'bg-white/10 text-gray-300' },
};
const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

// Admin — démarches administratives entreprise + paiements app (rappels par période)
export const BusinessTasksPanel = () => {
  const { at } = useAdminLang();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'fiscal', months: [] });

  const load = () => axios.get('/api/admin/business-tasks').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleDone = async (id) => {
    try { await axios.patch(`/api/admin/business-tasks/${id}/toggle-done`); load(); } catch { toast.error('Erreur'); }
  };
  const remove = async (id) => {
    if (!window.confirm(at('bizConfirmDelete') || 'Supprimer cette démarche ?')) return;
    try { await axios.delete(`/api/admin/business-tasks/${id}`); load(); } catch { toast.error('Erreur'); }
  };
  const add = async () => {
    if (!form.title.trim() || !form.months.length) { toast.error(at('bizMissing') || 'Titre et mois requis'); return; }
    try {
      await axios.post('/api/admin/business-tasks', form);
      setShowAdd(false);
      setForm({ title: '', description: '', category: 'fiscal', months: [] });
      load();
    } catch { toast.error('Erreur'); }
  };

  if (!data) return <p className="text-xs text-gray-500">…</p>;
  const dueNow = new Set(data.due_now_ids || []);

  return (
    <div data-testid="business-tasks-panel">
      {/* Due this month */}
      {dueNow.size > 0 && (
        <div className="mb-3 p-3 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="biz-due-now">
          <p className="text-xs font-bold text-red-300 flex items-center gap-1.5 mb-1">
            <AlarmClock className="w-3.5 h-3.5" /> {at('bizDueNow') || 'À faire ce mois-ci'} ({dueNow.size})
          </p>
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {(data.items || []).map(t => {
          const cat = CATS[t.category] || CATS.other;
          const urgent = dueNow.has(t.id);
          return (
            <div key={t.id} className={`p-3 rounded-xl border ${urgent ? 'bg-red-500/5 border-red-500/40' : t.done_this_period ? 'bg-emerald-500/5 border-emerald-500/25 opacity-70' : 'bg-white/5 border-white/10'}`} data-testid={`biz-task-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className={`text-sm font-semibold ${t.done_this_period ? 'line-through text-gray-400' : 'text-white'}`}>{t.title}</p>
                  {t.description && <p className="text-[11px] text-gray-500 mt-0.5">{t.description}</p>}
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${cat.cls}`}>{cat.label}</span>
                    <span className="text-[10px] text-gray-500">{(t.months || []).map(m => MONTHS[m - 1]).join(' · ')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {t.due_this_month && (
                    <button onClick={() => toggleDone(t.id)} className={`p-2 rounded-full ${t.done_this_period ? 'bg-emerald-500/30 text-emerald-300' : 'bg-white/10 text-gray-300 hover:bg-emerald-500/20'}`} title={at('bizMarkDone') || 'Marquer fait'} data-testid={`biz-done-${t.id}`}>
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => remove(t.id)} className="p-2 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/25" data-testid={`biz-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add */}
      {showAdd ? (
        <div className="mt-3 p-3 rounded-xl bg-white/5 border border-white/10 space-y-2" data-testid="biz-add-form">
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={at('bizTitlePh') || 'Titre de la démarche'} className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="biz-add-title" />
          <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={at('bizDescPh') || 'Description (optionnel)'} className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
          <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex flex-wrap gap-1">
            {MONTHS.map((m, i) => (
              <button key={m} data-testid={`biz-add-month-${i + 1}`} onClick={() => setForm(f => ({ ...f, months: f.months.includes(i + 1) ? f.months.filter(x => x !== i + 1) : [...f.months, i + 1] }))} className={`px-2 py-1 rounded-lg text-[10px] ${form.months.includes(i + 1) ? 'bg-fuchsia-600 text-white' : 'bg-white/10 text-gray-400'}`}>{m}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={add} className="flex-1 py-2 bg-fuchsia-600 rounded-lg text-sm font-semibold text-white" data-testid="biz-add-save">{at('bizAdd') || 'Ajouter'}</button>
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-white/10 rounded-lg text-sm text-gray-300">✕</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="mt-3 w-full py-2.5 border border-dashed border-white/20 rounded-xl text-sm text-gray-400 hover:border-fuchsia-500/50 hover:text-white flex items-center justify-center gap-1.5" data-testid="biz-add-btn">
          <Plus className="w-4 h-4" /> {at('bizAddTask') || 'Ajouter une démarche'}
        </button>
      )}
    </div>
  );
};

export default BusinessTasksPanel;
